// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useState, useEffect, useCallback } from 'react';
import {
  Alert,
  Badge,
  Button,
  DataTable,
  StatusBanner,
} from '@loa/ui-components';
import type { DataTableColumn } from '@loa/ui-components';
import { formatDateTime } from '@loa/theme';
import {
  fundsApi,
  FundRun,
  RunHistoryResponse,
  AggregatorStatus,
} from '../api/fundsApi';
import FundRunDetailModal, {
  RUN_STATUS_LABELS,
  runStatusColor,
  formatDuration,
} from '../components/FundRunDetailModal';
import './FundsRetrievalHistoryPage.css';

const PAGE_SIZE = 20;

function FundsRetrievalHistoryPage() {
  const [runs, setRuns] = useState<FundRun[]>([]);
  const [history, setHistory] = useState<RunHistoryResponse | null>(null);
  const [status, setStatus] = useState<AggregatorStatus | null>(null);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [triggering, setTriggering] = useState(false);

  const [detailRun, setDetailRun] = useState<FundRun | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);

  const loadRuns = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      // Fetch one extra row: the aggregator's /runs is skip/limit with no
      // total count, so the spare row is the "there is a next page" signal.
      const data = await fundsApi.getRunHistory(PAGE_SIZE + 1, page * PAGE_SIZE);
      setHasNextPage(data.runs.length > PAGE_SIZE);
      setRuns(data.runs.slice(0, PAGE_SIZE));
      setHistory(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load fund retrieval history');
      setRuns([]);
      setHistory(null);
    } finally {
      setLoading(false);
    }
  }, [page]);

  const loadStatus = useCallback(async () => {
    try {
      setStatus(await fundsApi.getStatus());
    } catch {
      // The status strip is auxiliary — the runs table is the page's core,
      // so status failures stay silent rather than doubling the error banner.
      setStatus(null);
    }
  }, []);

  useEffect(() => {
    loadRuns();
  }, [loadRuns]);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  const handleTriggerRefresh = async () => {
    try {
      setTriggering(true);
      setError(null);
      await fundsApi.triggerRefresh();
      setSuccessMessage(
        'Retrieval run triggered. It can take several minutes to finish — reload to follow progress.'
      );
      await loadRuns();
      await loadStatus();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to trigger a retrieval run');
    } finally {
      setTriggering(false);
    }
  };

  const openDetail = (run: FundRun) => {
    setDetailRun(run);
    setDetailOpen(true);
  };

  const columns: DataTableColumn<FundRun>[] = [
    {
      key: 'started_at',
      header: 'Started',
      width: '180px',
      render: (run) => formatDateTime(run.started_at),
    },
    {
      key: 'status',
      header: 'Status',
      width: '140px',
      render: (run) => (
        <Badge color={runStatusColor(run.status)} size="sm">
          {RUN_STATUS_LABELS[run.status]}
        </Badge>
      ),
    },
    {
      key: 'trigger',
      header: 'Trigger',
      width: '110px',
      render: (run) => run.trigger,
    },
    {
      key: 'scrapers',
      header: 'Providers',
      width: '110px',
      render: (run) => `${run.scrapers_passed}/${run.total_scrapers} passed`,
    },
    {
      key: 'total_funds',
      header: 'Funds',
      width: '100px',
      render: (run) => run.total_funds.toLocaleString(),
    },
    {
      key: 'duration',
      header: 'Duration',
      width: '110px',
      render: (run) => formatDuration(run.duration_seconds),
    },
    {
      key: 'synced',
      header: 'Search catalogue',
      width: '150px',
      render: (run) =>
        history?.lastSyncedRunId === run.run_id ? (
          <Badge color="green" size="sm" variant="outlined">
            Synced
          </Badge>
        ) : (
          <span className="funds-history__muted">—</span>
        ),
    },
  ];

  return (
    <div className="funds-history">
      <StatusBanner
        variant="success"
        message={successMessage}
        visible={!!successMessage}
        dismissible
        onDismiss={() => setSuccessMessage(null)}
        position="inline"
      />

      <div className="funds-history__header">
        <div>
          <h2 className="funds-history__title">Funds Retrieval History</h2>
          <p className="funds-history__subtitle">
            Scrape runs executed by the fund aggregator across all providers. The catalogue that
            advisors search is refreshed from the most recent successful run.
          </p>
        </div>
        <Button
          size="md"
          onClick={handleTriggerRefresh}
          loading={triggering}
          disabled={triggering}
        >
          Refresh now
        </Button>
      </div>

      <div className="funds-history__status-strip" data-testid="funds-status-strip">
        <div className="funds-history__status-item">
          <span className="funds-history__status-label">Next scheduled run</span>
          <span className="funds-history__status-value">
            {status?.next_run_time ? formatDateTime(status.next_run_time) : 'Unknown'}
          </span>
        </div>
        <div className="funds-history__status-item">
          <span className="funds-history__status-label">Last synced to catalogue</span>
          <span className="funds-history__status-value">
            {history?.lastSyncedAt ? formatDateTime(history.lastSyncedAt) : 'Never'}
          </span>
        </div>
        <div className="funds-history__status-item">
          <span className="funds-history__status-label">Last sync outcome</span>
          <span className="funds-history__status-value">
            {history?.lastSyncStatus ?? '—'}
          </span>
        </div>
      </div>

      {error && (
        <Alert variant="error" header="Could not load retrieval history" visible>
          <p>{error}</p>
          <Button variant="outlined" size="sm" onClick={loadRuns}>
            Retry
          </Button>
        </Alert>
      )}

      <DataTable
        columns={columns}
        data={runs}
        rowKey={(run) => run.run_id}
        onRowClick={openDetail}
        loading={loading}
        skeletonRows={8}
        emptyState={<span>No retrieval runs recorded yet.</span>}
      />

      <div className="funds-history__pagination">
        <Button
          variant="outlined"
          size="sm"
          disabled={page === 0 || loading}
          onClick={() => setPage((p) => Math.max(0, p - 1))}
        >
          Previous
        </Button>
        <span className="funds-history__page-label">Page {page + 1}</span>
        <Button
          variant="outlined"
          size="sm"
          disabled={!hasNextPage || loading}
          onClick={() => setPage((p) => p + 1)}
        >
          Next
        </Button>
      </div>

      <FundRunDetailModal
        run={detailRun}
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
      />
    </div>
  );
}

export default FundsRetrievalHistoryPage;
